import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import wsService from '../../services/websocket';
import './NetworkAlertBanner.css';

const ttlMs = 15000;

const normalizeBanner = (payload, fallbackType) => {
  const severity = String(payload?.severity || 'INFO').toLowerCase();
  const title = payload?.title || payload?.type || fallbackType || 'Network update';
  const detail = payload?.detail || payload?.message || '';
  const anomalyId = payload?.anomalyId || payload?.anomaly_id;

  return {
    severity,
    title,
    detail,
    href: anomalyId ? `/app/admin/network/anomalies/${anomalyId}` : '/app/admin/network',
  };
};

const NetworkAlertBanner = () => {
  const { user } = useAuth();
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    if (!['admin', 'super_admin'].includes(user?.role)) {
      return undefined;
    }

    wsService.connect();

    const showBanner = (payload, fallbackType) => {
      setBanner(normalizeBanner(payload, fallbackType));
    };

    const unsubscribers = [
      wsService.subscribe('network:anomaly', (payload) => showBanner(payload, 'Network anomaly')),
      wsService.subscribe('network:health_alert', (payload) => showBanner(payload, 'Network health alert')),
      wsService.subscribe('network:device_offline', (payload) => showBanner(payload, 'Critical device offline')),
    ];

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [user?.role]);

  useEffect(() => {
    if (!banner) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setBanner(null);
    }, ttlMs);

    return () => window.clearTimeout(timer);
  }, [banner]);

  if (!banner) {
    return null;
  }

  return (
    <div className={`network-alert-banner severity-${banner.severity}`} role="status" aria-live="polite">
      <div className="network-alert-content">
        <strong>{banner.title}</strong>
        <span>{banner.detail || 'The backend reported a new network monitoring event.'}</span>
      </div>
      <div className="network-alert-actions">
        <Link to={banner.href}>Open</Link>
        <button type="button" onClick={() => setBanner(null)} aria-label="Dismiss network alert">
          Dismiss
        </button>
      </div>
    </div>
  );
};

export default NetworkAlertBanner;
